// @ts-nocheck
const RAGE_CLICK_COUNT = 5;
const RAGE_CLICK_WINDOW_MS = 2000;
const RAGE_CLICK_RADIUS_PX = 30;
const IDLE_AFTER_MS = 60 * 1000;
const SCROLL_THROTTLE_MS = 1000;
const API_SLOW_THRESHOLD_MS = 1000;

const MODAL_SELECTORS = [".modal.show", ".p-dialog", ".MuiModal-root"];

let emit = null;
let cleanupFns = [];
let recentClicks = [];
let idleTimer = null;
let idleSince = null;
let lastScrollAt = 0;

function deriveLabel(el) {
  try {
    let node = el;
    for (let depth = 0; node && depth < 4; depth++, node = node.parentElement) {
      const explicit = node.getAttribute?.("data-sr-label");
      if (explicit) return explicit;
      const aria = node.getAttribute?.("aria-label");
      if (aria) return aria;
      const tag = node.tagName?.toLowerCase();
      if (tag === "button" || tag === "a" || node.getAttribute?.("role") === "button") {
        const text = (node.innerText || node.textContent || "").trim();
        if (text) return text.slice(0, 60).replace(/\s+/g, " ");
      }
    }
    const tag = el.tagName?.toLowerCase() || "";
    const id = el.id ? `#${el.id}` : "";
    const cls =
      typeof el.className === "string" && el.className.trim()
        ? `.${el.className.trim().split(/\s+/).slice(0, 2).join(".")}`
        : "";
    return `${tag}${id}${cls}`.slice(0, 120);
  } catch {
    return "";
  }
}

function deriveSelector(el) {
  try {
    const parts = [];
    let node = el;
    for (let depth = 0; node && node.tagName && depth < 5; depth++, node = node.parentElement) {
      let part = node.tagName.toLowerCase();
      if (node.id) {
        parts.unshift(`${part}#${node.id}`);
        break;
      }
      const cls =
        typeof node.className === "string" && node.className.trim()
          ? `.${node.className.trim().split(/\s+/)[0]}`
          : "";
      parts.unshift(`${part}${cls}`);
    }
    return parts.join(" > ").slice(0, 300);
  } catch {
    return "";
  }
}

function normCoords(clientX, clientY) {
  const vw = window.innerWidth || 1;
  const vh = window.innerHeight || 1;
  return {
    x: Math.round((clientX / vw) * 10000),
    y: Math.round((clientY / vh) * 10000),
    viewport_w: vw,
    viewport_h: vh,
  };
}

function onActivity() {
  if (idleSince) {
    const idleSeconds = Math.round((Date.now() - idleSince) / 1000);
    idleSince = null;
    emit?.("idle_end", { payload: { idle_seconds: idleSeconds } });
  }
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idleSince = Date.now();
    emit?.("idle_start", {});
  }, IDLE_AFTER_MS);
}

function onClick(e) {
  try {
    onActivity();
    const target = e.target;
    const coords = normCoords(e.clientX, e.clientY);
    const label = deriveLabel(target);
    const selector = deriveSelector(target);

    emit?.("click", {
      ...coords,
      element_label: label,
      element_selector: selector,
    });

    const now = Date.now();
    recentClicks.push({ t: now, cx: e.clientX, cy: e.clientY });
    recentClicks = recentClicks.filter((c) => now - c.t <= RAGE_CLICK_WINDOW_MS);
    if (recentClicks.length >= RAGE_CLICK_COUNT) {
      const first = recentClicks[0];
      const within = recentClicks.every(
        (c) =>
          Math.abs(c.cx - first.cx) <= RAGE_CLICK_RADIUS_PX &&
          Math.abs(c.cy - first.cy) <= RAGE_CLICK_RADIUS_PX
      );
      if (within) {
        recentClicks = [];
        emit?.("rage_click", {
          ...coords,
          element_label: label,
          element_selector: selector,
          payload: { clicks: RAGE_CLICK_COUNT, window_ms: RAGE_CLICK_WINDOW_MS },
        });
      }
    }
  } catch {
  }
}

function onScroll() {
  const now = Date.now();
  if (now - lastScrollAt < SCROLL_THROTTLE_MS) return;
  lastScrollAt = now;
  onActivity();
  try {
    const doc = document.documentElement;
    const max = Math.max(1, doc.scrollHeight - window.innerHeight);
    const depth = Math.min(100, Math.round((window.scrollY / max) * 100));
    emit?.("scroll_depth", { payload: { depth_pct: depth } });
  } catch {
  }
}

function onVisibility() {
  emit?.(document.visibilityState === "hidden" ? "tab_blur" : "tab_focus", {});
}

function installModalObserver() {
  try {
    const isModal = (node) =>
      node?.nodeType === 1 &&
      MODAL_SELECTORS.some((sel) => node.matches?.(sel) || node.querySelector?.(sel));

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (isModal(node)) {
            emit?.("modal_open", {
              element_label: deriveLabel(node).slice(0, 120),
            });
            return;
          }
        }
        for (const node of m.removedNodes) {
          if (isModal(node)) {
            emit?.("modal_close", {});
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: false });
    return () => observer.disconnect();
  } catch {
    return () => {};
  }
}

export function noteApiCall(method, urlPath, status, durationMs) {
  try {
    if (!emit) return;
    const failed = !status || status >= 400;
    if (!failed && durationMs < API_SLOW_THRESHOLD_MS) return;
    emit("api_call", {
      payload: {
        method: String(method || "").toUpperCase(),
        url: String(urlPath || "").slice(0, 200),
        status: Number(status) || 0,
        duration_ms: Math.round(durationMs || 0),
      },
    });
  } catch {
  }
}

export function install(emitFn) {
  if (emit) uninstall();
  emit = emitFn;

  const listeners = [
    [document, "click", onClick, true],
    [window, "scroll", onScroll, true],
    [document, "visibilitychange", onVisibility, false],
    [document, "keydown", onActivity, true],
    [document, "mousemove", onActivity, true],
  ];
  for (const [target, type, fn, capture] of listeners) {
    target.addEventListener(type, fn, capture);
    cleanupFns.push(() => target.removeEventListener(type, fn, capture));
  }
  cleanupFns.push(installModalObserver());

  onActivity(); 
}

export function uninstall() {
  for (const fn of cleanupFns) {
    try {
      fn();
    } catch {
    }
  }
  cleanupFns = [];
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = null;
  idleSince = null;
  recentClicks = [];
  emit = null;
}
